'use client';

/**
 * Lead-Magnet HubSpot Form leaf
 *
 * Variant of @/components/hubspot/HubSpotForm that does NOT default `region`.
 * The shared HubSpotForm injects `region: 'na1'` whenever the prop is omitted,
 * which would inflate parity diffs against source HTML where prod's
 * `hbspt.forms.create({...})` config either includes `region` or not.
 *
 * Per the typed-CMS migration policy: preserve source byte-for-byte.
 *
 * If `region` is undefined the key is omitted from the create() call entirely.
 *
 * Originally extracted from src/components/blocks/webinar-signup/. The
 * webinar-signup module re-exports this component under its old name for
 * backwards compatibility.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { loadHubSpotForms, resolveHubSpotPortalId, HUBSPOT_PORTAL_ID } from '@/lib/hubspot-forms';
import { HUBSPOT_EMBEDS_DISABLED } from '@/lib/hubspot-embed-gate';
import { InertEmbedPlaceholder } from '@/components/hubspot/InertEmbedPlaceholder';

// The global `Window.hbspt` shape is already declared by
// @/components/hubspot/HubSpotForm. Don't redeclare here — just consume it.

export interface LeadMagnetHubSpotFormProps {
  formId: string;
  portalId?: string;
  /** Pass only when the source HTML's `hbspt.forms.create({...})` config includes a `region:` key. */
  region?: string;
  className?: string;
  /**
   * Defer the forms embed script until the container nears the viewport
   * (above-fold forms still load immediately after hydration). Defaults to
   * true — PERF CONTRACT, see tests/unit/perf/perf-guards.test.ts.
   */
  lazy?: boolean;
}

export function LeadMagnetHubSpotForm({
  formId,
  portalId = HUBSPOT_PORTAL_ID,
  region,
  className,
  lazy = true,
}: LeadMagnetHubSpotFormProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const instanceId = useId().replace(/:/g, '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [visible, setVisible] = useState(!lazy);

  useEffect(() => {
    if (!lazy || !containerRef.current) return;
    const el = containerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [lazy]);

  useEffect(() => {
    if (!visible || !containerRef.current) return;

    let mounted = true;

    loadHubSpotForms()
      .then(() => {
        if (!mounted || !containerRef.current) return;

        containerRef.current.innerHTML = '';

        // Build config conditionally — omit `region` entirely when not supplied.
        const config: Record<string, unknown> = {
          portalId: resolveHubSpotPortalId(portalId),
          formId,
          target: `#hubspot-form-${instanceId}`,
        };
        if (region) config.region = region;

        window.hbspt?.forms.create(config);
        setLoading(false);
      })
      .catch(() => {
        if (mounted) {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [visible, formId, portalId, region, instanceId]);

  // Staging gate — placed after the hooks so hook order stays stable, and
  // inert regardless: the effects above bail on a null containerRef, which is
  // what they see when the placeholder renders instead of the mount div.
  if (HUBSPOT_EMBEDS_DISABLED) {
    return (
      <InertEmbedPlaceholder
        label="HubSpot form"
        detail={`form ${formId} · portal ${portalId}`}
        className={className}
      />
    );
  }

  if (error) {
    return (
      <div className={className} style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
        Unable to load form. Please refresh the page or{' '}
        <a href="tel:828-483-4237" style={{ color: '#ED8326' }}>
          call us at 828-483-4237
        </a>
        .
      </div>
    );
  }

  return (
    <div className={className}>
      {loading && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#999' }}>Loading form…</div>
      )}
      <div ref={containerRef} id={`hubspot-form-${instanceId}`} />
    </div>
  );
}
